import type { RenderModelPage } from '@aline/common';
import type { CollisionDetection } from '@dnd-kit/core';
import { pointerWithin, rectIntersection } from '@dnd-kit/core';
import { Apollo } from 'src/gql/generated';
import { cannedFieldTypename, sigFieldTypename, variableTypename } from 'src/helpers';
import { z } from 'zod';

export const DragFieldSchema = z.discriminatedUnion('__typename', [
  z.object({
    __typename: z.literal(sigFieldTypename),
    nodeId: z.uuid(),
    signatureVariant: z.enum(Apollo.SignatureFieldVariant).optional(),
  }),
  z.object({
    __typename: z.literal(variableTypename),
    actionType: z.union([z.literal('create'), z.literal('insert')]),
    checkboxOptions: z
      .array(
        z.object({
          choiceRefId: z.uuid(),
          id: z.uuid(),
          optionText: z.string().optional(),
        })
      )
      .optional(),
    config: z.nullable(z.object({ id: z.uuid() })),
    integrationLink: z
      .object({
        attributePath: z.string(),
        integration: z.enum(Apollo.Integration),
      })
      .optional(),
    isDynamic: z.boolean(),
    label: z.string().optional(),
    nodeId: z.uuid(),
    options: z
      .array(
        z.object({
          optionText: z.string(),
          triggerAction: z.enum(Apollo.TriggerAction),
        })
      )
      .optional(),
    selectedAnswerIds: z.array(z.uuid()).optional(),
    value: z.string().nullable(),
    variant: z.enum(Apollo.MetadataVariant),
  }),
  z.object({
    __typename: z.literal(cannedFieldTypename),
    assigneeId: z.uuid().nullable(),
    nodeId: z.uuid(),
    variant: z.enum(Apollo.CannedFieldVariant),
  }),
]);
export type DragField = z.infer<typeof DragFieldSchema>;

const PageElementSchema: z.ZodType<RenderModelPage> = z.any();
const PathSchema = z.array(z.number());

export const DropFieldSchema = z.object({
  pageNumber: z.number(),
  rMPageEntry: z.tuple([PageElementSchema, PathSchema]),
  tabId: z.uuid(),
});

export type DropField = z.infer<typeof DropFieldSchema>;

/**
 * See https://github.com/clauderic/dnd-kit/issues/765.
 * We use pointerWithin (cursor-based) instead of reading dragEl.getBoundingClientRect() live.
 * Reading the overlay's DOM rect causes a render loop: overlay size changes -> different collision
 * result -> onDragMove fires -> drop location state changes -> overlay size changes -> repeat.
 */
export function createDetectDragPreviewCollisions(activeTabId: UUID | null): CollisionDetection {
  return args => {
    const droppableContainers = activeTabId
      ? args.droppableContainers.filter(container => {
          const data = container.data.current;
          return data && 'tabId' in data && data.tabId === activeTabId;
        })
      : [];
    if (args.pointerCoordinates) {
      return pointerWithin({ ...args, droppableContainers });
    }
    return rectIntersection({ ...args, droppableContainers });
  };
}
